import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { useDispatch, useSelector } from 'react-redux';
import { getCountryFlagsData } from 'aster-foundation/core/astercore/auth/login/redux/actions';
import { hitNotifyForDownloadAppLink } from 'aster-foundation/core/epharmacy/Product/redux/action';
import Link from 'next/link';
import { Row, Col, Form } from 'react-bootstrap';
import { countryCodeDefaultKSA, FLAGSMITH_KEYS, ONE_ASTER_CATEGORY, ONLINE_PHARMACY_ROUTE } from '@common/Utility/constants';
import { errorHandling, getConfigurationValue, getReferralPageForGA, MIN_MOBILE_WIDTH } from '@common/Utility/common-utils';
import { IC_ANDROID, IC_HUAWEI, IC_IOS, DOWNLOAD_APP_HAND_MOBILE, DOWNLOAD_APP_HAND_REVERSE } from '@common/Assets/images';
import CountryCodeInput from '@common/Component/CountryCodeInput';
import { ButtonBase } from '@common/Component/Forms';
import logger from '@common/Utility/logger';
import { useNavigationURL } from '@common/Utility/generateNavigationURL';
import { flagsmith } from '@common/Lib/Flagsmith';
import { isRTL, useTranslation } from '@common/i18n';
import { DownloadAppSectionStyled } from './DownloadAppSectionStyled';
import { getLangCodeFromUrl } from '../../../../../i18n';
import { dataLayerSendEvent } from 'Epharmacy/EpharmacyAnalytics';
import { useRouter } from 'next/router';
import { toast } from 'react-toastify';

const DownloadAppSection = (props) => {
  const appConfigData = useSelector((state) => state?.astercore?.AppConfig?.data?.data);
  const countryData = useSelector((state) => state?.astercore?.getCountryFlagsData?.data);
  const [mobileNumber, setMobileNumber] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentLang, setCurrentLanguage] = useState('');
  const [isMobile, setIsMobile] = React.useState(false);
  const dispatch = useDispatch();
  const generateURL = useNavigationURL();
  const t = useTranslation();
  const router = useRouter();
  const web_feature_flaging = flagsmith.getValue(FLAGSMITH_KEYS.WEB_FEATURE_FLAGING, {json: true});
  const referralPageForGa = router?.asPath ? getReferralPageForGA(router?.asPath) : 'Aster Core Login ';

  const handleResize = () => {
    if (window.innerWidth <= MIN_MOBILE_WIDTH) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };
  useEffect(() => {
    if(typeof window !== 'undefined'){
      setCurrentLanguage(getLangCodeFromUrl());
    }
    window?.addEventListener('resize', handleResize);
    return () => {
      window?.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setIsLoading(true);
    dispatch(
      getCountryFlagsData({
        onSuccess: () => {
          setIsLoading(false);
        },
        onFailure: (err) => {
          logger.error('🚀 ~ file: download app popup modal ~ DownloadAppSection.js ~ useEffect ~ err:', err);
          setIsLoading(false);
          errorHandling(err);
        },
      }),
    );
  }, []);

  const sendDownloadAppLink = () => {
    const requestParams = {
      mobile: mobileNumber?.countryCode + mobileNumber?.number
    };
    setIsLoading(true);
    dispatch(
      hitNotifyForDownloadAppLink({
        requestParams,
        onSuccess: (response) => {
          if (response?.success) {
            toast.success(response?.body?.message);
            setMobileNumber('');
            setIsLoading(false);
          }
        },
        onFailure: (e) => {
          errorHandling(e);
          setIsLoading(false);
        },
      }),
    );
  };

  const mobileNoErrorEvent = (error = '', errType = '') => {
      dataLayerSendEvent('mobile_error', {
        screen_name: props?.screenName,
        page_type: props?.pageType,
        location: "pdp card",
        myaster_category: ONE_ASTER_CATEGORY.PHARMACY,
        referrer_page: referralPageForGa || '',
        item_name: props?.itemName,
        item_id: props?.id,
        country_code: mobileNumber?.countryCode || '',
        error,
        err_type: errType
      });
    }
  
  const onSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }
    try {
      if (mobileNumber.isNumberValid && mobileNumber.number) {
        dataLayerSendEvent('mobile_number_add', {
          screen_name: props?.screenName,
          page_type: props?.pageType,
          location: "pdp card",
          myaster_category: ONE_ASTER_CATEGORY.PHARMACY,
          referrer_page: referralPageForGa || '',
          item_name: props?.itemName,
          item_id: props?.id,
          country_code: mobileNumber?.countryCode,
          delivery_promise: props?.deliveryPromise,
          express_availability: props?.expressAvailability
        });
        setErrorMsg('');
        setIsLoading(true);
        sendDownloadAppLink();
      } else if (!mobileNumber.number) {
        setErrorMsg(t('DownloadAppSection.enterPhoneNumberError'));
        mobileNoErrorEvent(t('DownloadAppSection.enterPhoneNumberError'), 'validation')
      } else {
        setErrorMsg(t('DownloadAppSection.validPhoneNumberError'));
        mobileNoErrorEvent(t('DownloadAppSection.validPhoneNumberError'), 'validation')
      }
    } catch (err) {
      setIsLoading(false);
      errorHandling(err);
      mobileNoErrorEvent(err?.data?.message || '', 'api');
    }
  }

  const onDownloadButtonClick = (buttonName = '') => {
    dataLayerSendEvent('app_download_button_click', {
      screen_name: props?.screenName,
      page_type: props?.pageType,
      myaster_category: ONE_ASTER_CATEGORY.PHARMACY,
      button_location: 'pdp card',
      button_name: buttonName
    });
  }

  return (
    <DownloadAppSectionStyled className={`${currentLang && isRTL(currentLang) ? 'rtl' : ''}`}>
      <div className="title">{t('DownloadAppSection.completeOrder')} {!isMobile && t('DownloadAppSection.getTheLink')}</div>
      {!isMobile &&
        <Form onSubmit={onSubmit} noValidate>
          <div className="countryCodeInputWrapper">
            <div className="countryCodeInputInnerWrapper">
              <CountryCodeInput
                showLabel
                placeholder={t('DownloadAppSection.mobileNumber')}
                countryData={countryData ? Object.values(countryData) : countryCodeDefaultKSA}
                setMobileNumber={setMobileNumber}
                errorMsg={errorMsg}
                darkMode={true}
              />
            </div>
            <div>
              <ButtonBase
                data-test="button4"
                enableFullWidth
                size="md"
                variant="success"
                text={t('submit')}
                type="submit"
                customClass="submitCTA"
                disabled={isLoading || !mobileNumber}
              />
            </div>
          </div>
        </Form>
      }
      <div className='app-icon-wrapper'>
        {web_feature_flaging?.isAndroidStoreLinkEnable &&
          <Link
            passHref
            href={
              getConfigurationValue(appConfigData, 'androidPatientAppUrl')
                ? `${getConfigurationValue(appConfigData, 'androidPatientAppUrl')}`
                : generateURL(ONLINE_PHARMACY_ROUTE)
            }
          >
            <a onClick={() => onDownloadButtonClick('Google Play')} target="_blank">
              <Image src={IC_ANDROID} alt="" />
            </a>
          </Link>
        }
        {web_feature_flaging?.isIOSStoreLinkEnable &&
          <Link
            passHref
            href={
              getConfigurationValue(appConfigData, 'iosPatientAppUrl')
                ? `${getConfigurationValue(appConfigData, 'iosPatientAppUrl')}`
                : generateURL(ONLINE_PHARMACY_ROUTE)
            }
          >
            <a target="_blank" onClick={() => onDownloadButtonClick('App Store')}>
              <Image src={IC_IOS} alt="" />
            </a>
          </Link>
        }
        {web_feature_flaging?.isHuaweiStoreLinkEnable &&
          <Link
            passHref
            href={
              getConfigurationValue(appConfigData, 'huaweiPatientAppUrl')
                ? `${getConfigurationValue(appConfigData, 'huaweiPatientAppUrl')}`
                : generateURL(ONLINE_PHARMACY_ROUTE)
            }
          >
            <a target="_blank">
              <Image src={IC_HUAWEI} alt="" />
            </a>
          </Link>
        }
        {!isMobile &&
          <div className={`hand-mobile-img-wrappoer`}>
            <Image src={currentLang && isRTL(currentLang) ? DOWNLOAD_APP_HAND_REVERSE : DOWNLOAD_APP_HAND_MOBILE} alt="" />
          </div>
        }
      </div>
    </DownloadAppSectionStyled>
  );
}
export default DownloadAppSection;
